import React, { useEffect, useRef, useState } from 'react';
import { Target, Lightbulb, Rocket } from 'lucide-react';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, []);

  const values = [
    {
      icon: Target,
      title: "Creator-First Approach",
      description: "You remain the face and voice. We handle the complexity behind the scenes, ensuring your authenticity stays intact."
    },
    {
      icon: Lightbulb,
      title: "Innovation in Monetization",
      description: "We create digital products your audience actually wants — moving beyond traditional brand deals to sustainable income."
    },
    {
      icon: Rocket,
      title: "Zero-Risk Partnership",
      description: "No upfront costs, no hidden fees. We invest in your success and only win when you do."
    }
  ];

  return (
    <section id="about" ref={sectionRef} className="py-24 px-6 bg-[rgb(26,28,30)] relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[rgba(124,56,178,0.05)] blur-[120px] rounded-full"></div>

      <div className="max-w-[1200px] mx-auto relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 text-[rgb(197,155,47)] text-sm font-semibold mb-4">
            <div className="w-8 h-[2px] bg-gradient-to-r from-transparent to-[rgb(197,155,47)]"></div>
            <span>ABOUT US</span>
            <div className="w-8 h-[2px] bg-gradient-to-l from-transparent to-[rgb(197,155,47)]"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Who We Are & <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">What We Do</span>
          </h2>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 mb-16">
          {/* Left Column */}
          <div className={`space-y-6 transition-all duration-700 delay-100 max-w-[700px] ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <h3 className="text-3xl font-bold text-white">
              Empowering Creators to Build <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Real Businesses</span>
            </h3>
            <p className="text-lg text-[rgb(218,218,218)] leading-relaxed">
              At Eshwald Ops, we believe creators deserve more than sporadic brand deals. You've built an engaged audience — now it's time to turn that trust into consistent, sustainable income.
            </p>
            <p className="text-lg text-[rgb(218,218,218)] leading-relaxed">
              We partner with creators to develop, package, and launch premium digital products tailored to your audience's needs. From e-books and courses to templates and exclusive content — we handle everything behind the scenes.
            </p>
            <p className="text-lg text-[rgb(218,218,218)] leading-relaxed">
              You stay focused on creating. We manage product development, launch strategies, customer support, and all backend operations. It's a true partnership where your success is our success.
            </p>

            {/* Verified / Certificate Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={() => setShowCertificate(true)}
                className="group flex items-center space-x-3 bg-gradient-to-r from-[rgba(197,155,47,0.1)] to-[rgba(124,56,178,0.1)] border border-[rgba(197,155,47,0.4)] hover:border-[rgb(197,155,47)] rounded-2xl px-6 py-4 transition-all duration-300 hover:shadow-[0_0_30px_rgba(197,155,47,0.25)] hover:scale-105"
              >
                <img src="/verified.png" alt="Verified" className="w-9 h-9 object-contain group-hover:scale-110 transition-transform duration-300 flex-shrink-0" />
                <span
                  className="text-[rgb(197,155,47)]"
                  style={{ fontFamily: "'Dancing Script', 'Pinyon Script', cursive", fontSize: '1.45rem', fontWeight: 600, letterSpacing: '0.02em' }}
                >
                  Consulting.com Certified
                </span>
              </button>
            </div>
          </div>

          {/* Stats tiles hidden for now */}
          {/* <div className={`grid grid-cols-2 gap-6 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] hover:border-[rgb(197,155,47)] rounded-2xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-[0_10px_30px_rgba(197,155,47,0.2)] group">
              <div className="text-5xl font-bold bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text mb-2 group-hover:scale-110 transition-transform duration-300">5+</div>
              <div className="text-[rgb(161,161,170)] text-sm">Active Creator Partnerships</div>
            </div>
            <div className="bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] hover:border-[rgb(197,155,47)] rounded-2xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-[0_10px_30px_rgba(197,155,47,0.2)] group">
              <div className="text-5xl font-bold bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text mb-2 group-hover:scale-110 transition-transform duration-300">15+</div>
              <div className="text-[rgb(161,161,170)] text-sm">Products Launched</div>
            </div>
            <div className="bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] hover:border-[rgb(197,155,47)] rounded-2xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-[0_10px_30px_rgba(197,155,47,0.2)] group">
              <div className="text-5xl font-bold bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text mb-2 group-hover:scale-110 transition-transform duration-300">280%</div>
              <div className="text-[rgb(161,161,170)] text-sm">Avg. Revenue Increase</div>
            </div>
            <div className="bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] hover:border-[rgb(197,155,47)] rounded-2xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-[0_10px_30px_rgba(197,155,47,0.2)] group">
              <div className="text-5xl font-bold bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text mb-2 group-hover:scale-110 transition-transform duration-300">100%</div>
              <div className="text-[rgb(161,161,170)] text-sm">Client Satisfaction</div>
            </div>
          </div> */}
        </div>

        {/* Values */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div
                key={index}
                className={`bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 hover:border-[rgb(197,155,47)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(197,155,47,0.2)] group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${(index + 4) * 100}ms` }}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[rgba(197,155,47,0.2)] to-[rgba(124,56,178,0.2)] border border-[rgba(197,155,47,0.3)] flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <Icon size={28} className="text-[rgb(197,155,47)]" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-3 group-hover:text-[rgb(197,155,47)] transition-colors duration-300">{value.title}</h4>
                <p className="text-[rgb(161,161,170)] leading-relaxed">{value.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Certificate Modal */}
      {showCertificate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setShowCertificate(false)}
        >
          <div
            className="relative"
            style={{ width: '50vw', maxWidth: '900px', minWidth: '320px' }}
            onClick={e => e.stopPropagation()}
          >
            <img
              src="/certificate.jpg"
              alt="Certificate"
              className="w-full rounded-2xl shadow-[0_0_60px_rgba(197,155,47,0.3)]"
            />
            <button
              onClick={() => setShowCertificate(false)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[rgb(197,155,47)] text-[rgb(17,17,19)] flex items-center justify-center font-bold text-lg hover:bg-[rgb(224,194,95)] transition-colors duration-200 shadow-lg"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default About;
